<template>
	<div
		class="workflow-container-header"
		:id="`${stepNumber}-wfheader`"
		data-print="enabled"
		:class="{disabled: disabledClass}"
	>
		<div class="top">
			<div class="left-side">
				<header>
					<!-- custom step header -->
					<div v-if="!!customStep" class="input custom-name step-header">
						<h6 class="mobile-subtitle-2" data-cy="step-id">
							{{ stepNumber }}
						</h6>
						<h6
							class="mobile-subtitle-2"
							data-cy="step-title"
							data-testid="custom-product-name"
							placeholder="Name of step"
							role="input"
							@click="showEdit"
							v-show="!isShowEdit"
						>
							{{ getStepName }}
						</h6>
						<input
							ref="stepTitleInput"
							class="step-title-input"
							type="text"
							maxlength="300"
							v-model="customStepEditableName"
							v-show="isShowEdit"
							@blur="handleBlur"
						/>
					</div>
					<!-- qiagen step header -->
					<div class="custom-name" v-else>
						<h6 class="mobile-subtitle-2" data-cy="step-id">
							{{ stepNumber }}
						</h6>
						<h6 class="mobile-subtitle-2" data-cy="step-title">
							{{ getStepName }}
						</h6>
					</div>
				</header>
			</div>

			<i class="icons base-icons icons-invisible pdf-icon"></i>

			<div data-print="enabled" v-show="!isLabNotesActive" class="right-side toggle-switch">
				<md-button
					v-if="!customStep"
					id="add-product"
					class="md-theme-default has-icon"
					@click="tourActive ? '#' : addProduct()"
					:class="{disabled: !stepEnabled}"
				>
					<div>
						<base-icon icon-name="add" />
						<span class="mobile-hide">Add Product</span>
					</div>
				</md-button>
				<md-button
					id="show-hide-step"
					class="md-theme-default has-icon mobile-hide"
					@click="toggleEnabledState"
					:class="{disabled: !stepEnabled}"
				>
					<div
						v-if="!disabledClass"
						data-print="disabled"
						:content="$t('message.hideWfStep')"
						v-tippy="{
							distance: 12,
							placement: 'bottom',
							interactive: true,
						}"
					>
						<base-icon icon-name="visible " />
						Hide step
					</div>
					<div
						v-else-if="disabledClass"
						:content="$t('message.showWfStep')"
						v-tippy="{
							distance: 12,
							placement: 'bottom',
							interactive: true,
						}"
					>
						<base-icon icon-name="invisible" data-print="disabled" />
						<span>Show step</span>
					</div>
				</md-button>
			</div>
		</div>
		<div class="bottom">
			<step-tags :workflow="workflow"></step-tags>
		</div>
	</div>
</template>
<script>
import {trackEvent} from '@/services/tracking.js';
import StepTags from './StepTags.vue';

export default {
	components: {StepTags},
	name: 'StepHeader',
	props: {
		stepNumber: {
			type: Number,
		},
		customStep: {
			type: Object,
		},
		workflow: {
			type: Object,
		},
		stepEnabled: {
			type: Boolean,
			defualt: true,
		},
		isLabNotesActive: {
			type: Boolean,
			default: false,
		},
		tourActive: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			customStepEditableName: '',
			isShowEdit: '',
		};
	},
	methods: {
		showEdit() {
			this.isShowEdit = true;
			this.customStepEditableName = this.getStepName;
			this.$nextTick(() => {
				this.$refs?.stepTitleInput.focus();
			});
		},
		updateCustomStepTitle() {
			if (this.workflow.name === this.customStepEditableName) return;

			// if change and event is blur then update workflow obj and emit change
			this.$store.dispatch('workflow/setCustomStepName', {
				oldName: this.workflow.name,
				name: this.customStepEditableName,
			});
			this.$root.$emit('changeRegistered');
		},
		handleBlur(e) {
			if (this.customStepEditableName.trim() === '') this.customStepEditableName = this.workflow.name;
			this.updateCustomStepTitle();
			this.isShowEdit = false;
		},
		toggleEnabledState(e) {
			if (this.workflow?.customContent) {
				// toggle state if custom step
				this.$store.dispatch('workflow/setToggleEnabledState', this.workflow.name);
			} else if (this.workflow.selected?.length && this.workflow.selected[0]?.initial) {
				// toggle state if  step has selected products
				this.$store.dispatch('workflow/setToggleEnabledState', this.workflow.name);
				// run wfs rules
				this.$root.$emit('changeRegistered');
			} else {
				// open options modal
				trackEvent('ctaClick', {eVar58: 'See All Products | Workflow Configurator'});
				this.$emit('seeAllProducts', 0);
			}
		},
		addProduct() {
			trackEvent('ctaClick', {eVar58: 'Add Product | Workflow Configurator'});
			this.$emit('seeAllProducts', this.workflow.selected.length);
		},
	},
	computed: {
		getStepName() {
			if (!this.$store.state.cvlWebSteps) return;
			const checkForName = this.$store.state.cvlWebSteps.values.find((step) => step.key === this.workflow.name);
			const name = checkForName ? checkForName.value.enUS : this.workflow.name;
			if (!name) return '';
			return name;
		},
		disabledClass() {
			// add disabled class to steps that are disabled or do not have selected products
			return !this.stepEnabled ? true : this.emptyWorkflow;
		},
		emptyWorkflow() {
			return !!(this.workflow?.selected && !this.workflow.selected?.length);
		},
	},
};
</script>
<style lang="scss">
.workflow-container-header {
	@include flexRow;
	width: 100%;
	min-height: 47px;
	justify-content: space-between;
	align-items: center;
	background: $color-super-light-blue;
	border-radius: 4px 4px 0 0;
	border-top: 1px solid $color-light-grey;
	padding: 8px 16px;
	flex-wrap: wrap;
	.top {
		width: 100%;
		display: flex;
		justify-content: space-between;
	}
	.bottom {
		width: 100%;
		display: flex;
		@media print{
			display: none;
		}
	}
	.step-header {
		display: flex;
	}
	@media only screen and (min-width: $mobile+1) {
		padding: 8px 8px 8px 16px;
	}
	.toggle-switch {
		color: $color-blue;
	}
	.left-side {
		@include flexRow;
		max-width: 86%;
		width: 100%;
		header {
			width: 100%;
		}
		h6 {
			display: inline;
			margin: 0;
			position: relative;
			top: 2px;
			max-width: calc(100% - 32px);
			&:nth-child(1) {
				margin-right: 16px;
			}
			&:nth-child(2) {
				white-space: nowrap;
				overflow: hidden !important;
				text-overflow: ellipsis;
			}
		}
		input.step-title-input {
			top: 2px;
			position: relative;
			padding-left: 0;
			border: none;
			background-color: transparent;
			width: 100%;

			font-family: 'Futura W01 Medium', Arial, Helvetica, sans-serif !important;
			font-weight: 500;
			font-style: normal;
			color: rgba(0, 0, 0, 0.72);
			font-size: 1.25rem;
			line-height: 1.5;
			letter-spacing: 0.0075rem;
		}
		.custom-name {
			display: flex;
			justify-content: flex-start;
			align-items: center;
			margin-bottom: 0;

			&:hover {
				cursor: text;
			}
			&:focus {
				outline: none;
				border: none;
			}
			&[contenteditable][placeholder]:empty:before {
				content: attr(placeholder);
				position: absolute;
				color: $color-grey;
				font-style: oblique;
				background-color: transparent;
			}
		}
	}
	.right-side {
		#show-hide-step,
		#add-product {
			&:hover:before {
				background-color: $color-light-blue;
				opacity: 1;
			}
			div {
				display: flex;
				align-items: center;
				justify-content: center;

				color: $color-blue;
				position: relative;
				z-index: 1;
				.base-icons {
					margin-right: 0;
				}
				@media (min-width: $mobile + 1) {
					height: 32px;
					.base-icons {
						font-size: 14px;
						margin-right: 8.67px;
						margin-left: 0.67px;
					}
				}
			}
			&.disabled {
				&:hover:before {
					background-color: $color-mid-grey;
					opacity: 0.2;
				}
				div {
					color: $color-grey;
				}
			}
			@media (max-width: $mobile) {
				width: 32px;
				min-width: 32px;
				height: 32px;
				border-radius: 50%;
				.md-ripple {
					padding: 4.5px 12px;
				}
			}
		}
	}

	&.disabled {
		border-color: transparent;
		background: $color-light-grey;
		h6 {
			color: $color-grey;
		}
	}
}
</style>
